import { Button, Dropdown, message } from 'antd';
import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import DangerButton from '../../shared/components/buttons/DangerButton';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import { PAGE_SIZE } from '../../shared/constants';
import AddCircleOutlineIcon from '../../shared/svgs/AddCircleOutlineIcon';
import { doDeleteSupplier, getSuppliersList } from '../../api/suppliers';
import { SupplierI, SupplierSearchParamsT } from '../../interfaces/supplier';
import SuppliersFilterForm from '../../shared/components/forms/supplier/Filter';
import SuppliersTable from '../../shared/components/tables/Suppliers';
import { CurrentUserI } from '../../interfaces/auth';
import { handleAccessControl, handleFilterParams } from '../../utils/global';

const SuppliersPage: React.FC = () => {
  const currentUser: CurrentUserI = useOutletContext();
  const access = handleAccessControl(currentUser.roles, 'suppliers');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const urlSearch = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryParams = useMemo(() => {
    const start = Number(urlSearch.get('start')) ?? 0;
    const count = Number(urlSearch.get('count')) !== 0 ? Number(urlSearch.get('count')) : PAGE_SIZE;
    const search = urlSearch.get('filter[name]');
    const orderBy = urlSearch.get('orderBy');
    const orderType = urlSearch.get('orderType');
    return {
      start,
      count,
      filter: {
        name: search,
      },
      orderBy,
      orderType,
    };
  }, [urlSearch]) as SupplierSearchParamsT;
  const { data, isLoading } = useQuery(['suppliers', 'list', queryParams], () => getSuppliersList(queryParams));

  const currentPage = queryParams.start ? Math.round((Number(queryParams.start) - 1) / PAGE_SIZE + 1) : 1;

  const pagination: any = {
    current: currentPage,
    pageSize: PAGE_SIZE,
    total: data?.total || 0,
    showTotal: (total: string) => `${total} Items`,
  };

  const setQueryParams = (params: any) => {
    const query = handleFilterParams(params);
    navigate(`?${query}`);
  };

  const handleFilter = (params: any) => {
    const currentPage = params.page;
    const offset = currentPage ? (currentPage === 1 ? 0 : (Number(currentPage) - 1) * PAGE_SIZE) : 0;
    const newQueryParams: SupplierSearchParamsT = { ...queryParams, start: offset, count: params.per_page };

    Object.keys(params).forEach((key) => {
      if (key.startsWith('sorts[')) {
        const fieldName = key.replace('sorts[', '').replace(']', '');
        const sortType = params[key].toUpperCase();
        newQueryParams.orderBy = fieldName as 'name';
        newQueryParams.orderType = sortType;
      }
    });
    setQueryParams(newQueryParams);
  };

  const { mutateAsync: mutationDelete } = useMutation(doDeleteSupplier, {
    onSuccess: async () => {
      message.info('Deleted!');
      queryClient.invalidateQueries({
        queryKey: ['suppliers', 'list'],
      });
    },
  });

  const handleClickDelete = async (record: SupplierI) => {
    await mutationDelete(record.id);
    queryClient.invalidateQueries({
      queryKey: ['suppliers', 'list'],
    });
  };

  const handleFilterSubmit = (values: any) => {
    setQueryParams({ ...queryParams, filter: values });
  };

  const handleResetFilter = () => {
    setQueryParams({ start: 0, count: PAGE_SIZE, filter: undefined });
  };

  const withAction = (record: any) => {
    return (
      <span className="action" style={{ display: 'flex' }}>
        <Dropdown.Button
          overlayClassName="table-action-menu"
          menu={{
            items: [
              access.isCanRead
                ? {
                    key: '1',
                    label: (
                      <Link to={`/suppliers/${record?.id}`}>
                        <Button className="btn-table-action-primary" type="link">
                          View
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanUpdate
                ? {
                    key: '2',
                    label: (
                      <Link to={`/suppliers/${record?.id}/edit`}>
                        <Button className="btn-table-action-primary" type="link">
                          Edit
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanDelete
                ? {
                    key: '3',
                    label: <DangerButton label="Delete" onOk={() => handleClickDelete(record)} />,
                  }
                : null,
            ],
          }}
        />
      </span>
    );
  };

  return (
    <div className="suppliers-container">
      <div className="page-container">
        <div className="page-header">
          <div></div>
          <div className="page-header-actions">
            {access.isCanCreate && (
              <Link to="/suppliers/new">
                <PrimaryButton label="Create Supplier" icon={<AddCircleOutlineIcon />} />
              </Link>
            )}
          </div>
        </div>
        <div className="page-content">
          <SuppliersFilterForm
            handleFilter={handleFilterSubmit}
            handleReset={handleResetFilter}
            queryParams={queryParams}
          />
          <SuppliersTable
            rowKey="id"
            loading={isLoading}
            pagination={pagination}
            dataSource={data?.data}
            withAction={withAction}
            onChange2={handleFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default SuppliersPage;
